// Bar layout — greedy non-overlap stacking on the visible time axis (a
// rolling 12-month window, or one quarter when zoomed).
// Each placed bar gets _left, _right (% of the window), _row (stack index),
// and _clipL/_clipR when its true span continues beyond the window.
//
// Initiatives with no dates at all (legacy quarter-only rows) fall back to
// spanning their target quarter, so they still render on the axis.

import type { Initiative } from "./types";
import { pctOfWindowRaw, toDate, quarterBounds, type TimeWindow } from "./constants";
import { dependencyGroups } from "./dependencies";
import { actualShipped } from "./baseline";

export type PlacedBar = Initiative & {
  _left: number;
  _right: number;
  _row: number;
  _clipL: boolean; // span continues before the visible FY
  _clipR: boolean; // span continues after the visible FY
};

// Minimum visual bar width (% of FY) so short/point-in-time spans stay legible.
// Anchored at the right edge. The drag preview applies the same rule so a bar
// never changes size while being grabbed.
export const MIN_BAR_WIDTH_PCT = 10;

// A dependent that starts at (or just before) its predecessor's end follows it
// on the same row instead of dropping to the next one: its left edge is nudged
// right by up to FOLLOW_SNAP_PCT (a minimum-width predecessor often reaches a
// little past the real handoff), as long as it keeps FOLLOW_MIN_WIDTH_PCT.
export const FOLLOW_SNAP_PCT = 4;
const FOLLOW_MIN_WIDTH_PCT = 6;
const ROW_GAP_PCT = 0.5;

// The calendar span a bar represents: started (or, pre-start, the target so
// planning items render as a compact bar anchored at their estimate) through
// shipped ?? target — the three-date model has no separate "end".
export function initiativeSpan(i: Initiative): { start: Date; end: Date } | undefined {
  const qb = quarterBounds(i.targetQ);
  const start = toDate(i.start) ?? toDate(i.target) ?? qb?.start;
  const end = toDate(actualShipped(i)) ?? toDate(i.target) ?? qb?.end;
  if (!start && !end) return undefined;
  return { start: start ?? end!, end: end ?? start! };
}

// Does this initiative belong on the visible window's canvas?
export function inWindow(i: Initiative, win: TimeWindow): boolean {
  const span = initiativeSpan(i);
  // No dates and no recognizable quarter: better a full-window fallback bar
  // in every view than silently vanishing from the timeline.
  if (!span) return true;
  return span.start < win.end && span.end >= win.start;
}

// priorityIds: initiatives whose "Depends on" sits in another group of the
// same lane. Their blocks are placed first (top rows) so the connector from
// the other group stays short.
export function layoutBars(initiatives: Initiative[], win: TimeWindow, priorityIds?: Set<string>): { placed: PlacedBar[]; rowCount: number } {
  const items = initiatives.filter(i => inWindow(i, win)).map(i => {
    const span = initiativeSpan(i) ?? { start: win.start, end: win.end };
    const rawLeft = pctOfWindowRaw(span.start, win);
    const rawRight = pctOfWindowRaw(span.end, win);
    const clipL = rawLeft < 0;
    const clipR = rawRight > 100;
    let left = Math.max(0, rawLeft);
    let right = Math.min(100, rawRight);
    // Minimum width so even single-day or degenerate spans stay legible,
    // anchored at the right edge so the bar still reads "this lands here."
    if (right - left < MIN_BAR_WIDTH_PCT) {
      left = Math.max(0, right - MIN_BAR_WIDTH_PCT);
    }
    return { ...i, _left: left, _right: right, _clipL: clipL, _clipR: clipR };
  });

  // Sort by left edge (earliest first) for tight greedy stacking — display
  // order everywhere is chronological.
  items.sort((a, b) => a._left - b._left);

  // Dependency chains ("Depends on") move as one block so they share a row.
  // Inside a chain, members are packed greedily into local sub-rows (only
  // overlapping links need a second one); the block then takes the first run
  // of consecutive rows where every sub-row fits. An unlinked bar is a block
  // of one sub-row — exactly the plain greedy stacking.
  const blocks = dependencyGroups(items).map(group => {
    const subEnds: number[] = [];
    const subStarts: number[] = [];
    const subLast: string[] = []; // id of the bar currently ending each sub-row
    const members = group.map(item => {
      // Right behind its predecessor: share its sub-row.
      const follow = item.dependsOnId ? subLast.indexOf(item.dependsOnId) : -1;
      if (follow !== -1) {
        const left = Math.max(item._left, subEnds[follow] + ROW_GAP_PCT);
        if (item._left >= subEnds[follow] - FOLLOW_SNAP_PCT && item._right - left >= FOLLOW_MIN_WIDTH_PCT) {
          item._left = left;
          subEnds[follow] = item._right;
          subLast[follow] = item.id;
          return { item, sub: follow };
        }
      }
      let sub = subEnds.findIndex(end => item._left >= end + ROW_GAP_PCT);
      if (sub === -1) {
        sub = subEnds.length;
        subStarts.push(item._left);
        subEnds.push(item._right);
      } else {
        subEnds[sub] = item._right;
      }
      subLast[sub] = item.id;
      return { item, sub };
    });
    const priority = !!priorityIds && group.some(i => priorityIds.has(i.id));
    return { members, subStarts, subEnds, left: group[0]._left, priority };
  });
  blocks.sort((a, b) => (Number(b.priority) - Number(a.priority)) || (a.left - b.left));

  const rowEnds: number[] = [];
  const fits = (r: number, b: typeof blocks[number]) =>
    b.subStarts.every((start, j) => r + j >= rowEnds.length || start >= rowEnds[r + j] + ROW_GAP_PCT);
  const placed: PlacedBar[] = [];
  for (const b of blocks) {
    let row = 0;
    while (!fits(row, b)) row++;
    b.subEnds.forEach((end, j) => { rowEnds[row + j] = end; });
    for (const m of b.members) placed.push({ ...m.item, _row: row + m.sub });
  }

  return { placed, rowCount: Math.max(1, rowEnds.length) };
}
